// ── Service de fluxos de Leads (orquestração de aplicação) ────────
// Concentra as REGRAS/orquestração que antes viviam no hook useLeads: criação
// com fallback + tags + webhook, propagação de responsável aos cards, exclusão
// lógica em cascata, movimentação com efeitos (webhook + auto-cancel) e passagem
// ao Closer. Compõe múltiplos domínios via deps injetadas no core.ts (mesmo
// padrão do FilterOptionsService) — NÃO importa @app nem conhece Supabase.
// A UI (useLeads) fica só com cache otimista/rollback.

import type { ILeadRepository } from '../ports/ILeadRepository';
import type { MktAction, NotifyMktConversionOpts } from '@modules/mkt';
import type { CreateLeadInput, LeadCreateContext, LeadTagInput, LeadUpdateInput, CardMover } from '../leadModel';
import type { ImportLeadRow } from '../leadImport';

export interface LeadFlowDeps {
  leadRepo: ILeadRepository;
  notifyMktConversion: (action: MktAction, opts: NotifyMktConversionOpts) => void | Promise<void>;
  addTagToLead: (leadId: string, tagId: string) => Promise<unknown>;
  assignActiveCardsOwner: (leadId: string, field: 'sdr_id' | 'closer_id', userId: string | null) => Promise<void>;
  deactivateCardsByLead: (leadId: string) => Promise<void>;
  moveCardToStage: (cardId: string, stageId: string) => Promise<void>;
  markCardLost: (cardId: string, lossReason: string) => Promise<{
    success?: boolean;
    error?: string;
    linked_card_id?: string;
  }>;
  getStageNames: (ids: string[]) => Promise<Record<string, string>>;
  listPendingActivityIdsForCard: (cardId: string) => Promise<string[]>;
  cancelActivitiesByIds: (ids: string[]) => Promise<unknown>;
  // Enterprise: resolve (cria ou reaproveita) a entidade empresa pelo nome e
  // devolve seu id, para vincular os leads importados via CSV. Opcional — fora
  // do Enterprise a composição não injeta e o import não vincula empresa.
  resolveEmpresaId?: (workspaceId: string, nome: string) => Promise<string | null>;
}

export interface MoveCardStageParams {
  cardId: string;
  newStageId: string;
  oldStageId: string | null;
  leadId: string | null;
  mover: CardMover | null;
}

export interface PassToCloserParams {
  workspaceId: string;
  leadId: string;
  sdrId: string | null;
  prevStatus: string | null;
}
export interface BulkLossCardInput {
  id: string;
  linked_card_id?: string | null;
  status?: string | null;
  ativo?: boolean | null;
  lost_at?: string | null;
}

export interface BulkLossLeadInput {
  id: string;
  label?: string | null;
  cards?: BulkLossCardInput[] | null;
}

export interface BulkLossFailure {
  leadId: string;
  label: string;
  message: string;
}

export interface BulkLossSkipped {
  leadId: string;
  label: string;
  message: string;
}

export interface BulkLossProgress {
  completed: number;
  total: number;
  leadId: string;
}

export interface BulkLossResult {
  total: number;
  succeeded: number;
  failed: number;
  skipped: number;
  successfulLeadIds: string[];
  failures: BulkLossFailure[];
  skippedLeads: BulkLossSkipped[];
}

// O que a RPC fez com o card do pipeline alvo. 'kept' é a regra de preservação:
// o lead estava sendo trabalhado e permaneceu exatamente onde estava.
export type ImportCardAction = 'created' | 'revived' | 'repositioned' | 'kept' | 'none';

// Resultado por linha da importação em massa.
export interface ImportLeadOutcome {
  rowNumber: number;
  label: string;
  status: 'created' | 'updated' | 'error';
  message?: string; // motivo do erro OU warnings da RPC (ex.: responsável não encontrado)
  // Id devolvido pela RPC. É o que permite disparar direto para a lista recém
  // importada, sem o usuário ter que reencontrá-la no filtro.
  leadId?: string | null;
  cardAction?: ImportCardAction;
}

export interface ImportLeadsResult {
  total: number;
  created: number;
  updated: number;
  failed: number;
  // Linhas que encontraram o lead em andamento e não o moveram. Contam também
  // em `updated` (os dados de contato foram enriquecidos), mas merecem número
  // próprio: sem ele a importação parece ter mexido em tudo.
  kept: number;
  results: ImportLeadOutcome[];
  // Falha depois que os leads já entraram (hoje: a atribuição de responsável).
  // Não é erro de importação — a carga vale, só ficou algo pela metade.
  warning?: string;
}

// Escolhas do modal que valem para o arquivo inteiro e não são campo do lead.
// `sdrId` undefined = não mexer (mantém a regra automática); string = esse
// responsável; null = ninguém.
export interface ImportLeadsOptions {
  sdrId?: string | null;
}

export class LeadFlowService {
  constructor(private readonly deps: LeadFlowDeps) {}

  // Cria via RPC de origem; se ela reclamar de lead_origin, cai no fluxo direto.
  // Depois aplica extras, vincula tags e notifica o MKT. Retorna o id criado.
  createLead = async (input: CreateLeadInput, ctx: LeadCreateContext): Promise<{ lead_id: string }> => {
    if (!ctx?.workspaceSlug) throw new Error('Workspace sem slug.');
    const myEmail = ctx.userEmail || null;
    // Enterprise: o lead criado pelo CRM fica com o CRIADOR como SDR — sem
    // sorteio aleatório. Passar sdr_email não-nulo faz o trigger Config/54
    // respeitar a atribuição e não randomizar. Fora do enterprise, mantém o
    // comportamento antigo (só auto-atribui quando o criador é SDR/Closer).
    const autoSdrEmail    = (ctx.enterprise || ctx.teamRole === 'sdr') && myEmail ? myEmail : null;
    const autoCloserEmail = ctx.teamRole === 'closer' && myEmail ? myEmail : null;

    let result: { lead_id?: string } | undefined;
    try {
      result = (await this.deps.leadRepo.createExternalLead({
        workspace_slug: ctx.workspaceSlug,
        nome: input.nome,
        funnel_nome: input.funnel_nome || null,
        stage_nome: input.stage_nome || null,
        email: input.email || null,
        email_secundario: input.email_secundario || null,
        telefone: input.telefone || null,
        cargo: input.cargo || null,
        area_trabalho: input.area_trabalho || null,
        renda_liquida: input.renda_liquida || null,
        renda_liquida_value: input.renda_liquida_value ?? null,
        patrimonio: input.patrimonio || null,
        patrimonio_value: input.patrimonio_value ?? null,
        valor_investido: input.valor_investido || null,
        canal: input.canal || null,
        origem: input.origem || null,
        sub_origem: input.sub_origem || null,
        campanha: input.campanha || null,
        publico: input.publico || null,
        criativo: input.criativo || null,
        observacoes: input.observacoes || null,
        sdr_email:    input.sdr_email    ?? autoSdrEmail,
        closer_email: input.closer_email ?? autoCloserEmail,
      })) as { lead_id?: string } | undefined;
    } catch (err) {
      if (!/lead_origin/i.test((err as Error)?.message || '')) throw err;
      result = await this.createDirect(input, ctx);
    }

    const leadId = result?.lead_id;
    if (!leadId) throw new Error('Lead criado mas a RPC nao retornou lead_id.');

    const extras: LeadUpdateInput = {};
    if (input.patrimonio) extras.patrimonio = input.patrimonio;
    if (input.patrimonio_value != null) extras.patrimonio_value = input.patrimonio_value;
    if (input.renda_liquida_value != null) extras.renda_liquida_value = input.renda_liquida_value;
    if (input.closer_id) extras.closer_id = input.closer_id;
    // Empresa (Modelo A): vinculada após criar o lead — vale para o caminho da
    // RPC e para o fallback direto, sem alterar a RPC criar_lead_externo.
    if (input.empresa_id) extras.empresa_id = input.empresa_id;
    // Qualificação da matriz: colunas novas, que a RPC também não conhece.
    // Mesmo motivo — gravar depois evita recriar `criar_lead_externo`.
    if (input.empresa_hoje) extras.empresa_hoje = input.empresa_hoje;
    if (input.principal_desafio) extras.principal_desafio = input.principal_desafio;
    if (Object.keys(extras).length > 0) await this.deps.leadRepo.patchLead(leadId, extras);

    if (Array.isArray(input.tags) && input.tags.length > 0) {
      await Promise.all(
        input.tags
          .filter((t): t is LeadTagInput & { id: string } => !!t?.id)
          .map(t => this.deps.addTagToLead(leadId, t.id).catch(err => {
            console.error('[LeadFlow] Erro ao vincular tag:', err);
          })),
      );
    }

    // Webhook MKT (n8n) — entrada de lead (fire-and-forget, non-fatal).
    this.deps.notifyMktConversion('entrada-lead', { leadId });
    return { lead_id: leadId };
  };

  // Fallback direto: resolve responsável pelo papel + cria lead com card inicial.
  private createDirect = (input: CreateLeadInput, ctx: LeadCreateContext): Promise<{ lead_id: string }> => {
    const sdrId    = input.sdr_id    || ((ctx.enterprise || ctx.teamRole === 'sdr') ? ctx.userId ?? null : null);
    const closerId = input.closer_id || (ctx.teamRole === 'closer' ? ctx.userId ?? null : null);
    return this.deps.leadRepo.createLeadWithInitialCard({
      workspaceId: ctx.workspaceId,
      nome: input.nome,
      email: input.email || null,
      email_secundario: input.email_secundario || null,
      telefone: input.telefone || null,
      cargo: input.cargo || null,
      area_trabalho: input.area_trabalho || null,
      renda_liquida: input.renda_liquida || null,
      renda_liquida_value: input.renda_liquida_value ?? null,
      patrimonio: input.patrimonio || null,
      patrimonio_value: input.patrimonio_value ?? null,
      canal: input.canal || null,
      origem: input.origem || null,
      sub_origem: input.sub_origem || null,
      observacoes: input.observacoes || null,
      status: input.status || null,
      sdrId: sdrId || null,
      closerId: closerId || null,
    });
  };

  // Importação em massa (CSV). Cada linha passa pela MESMA RPC de upsert
  // (`criar_lead_externo`) usada na criação avulsa — logo herda dedup por
  // email/telefone e a regra de preservação: o card do funil alvo só é criado,
  // revivido ou reposicionado quando está perdido, inativo ou sem movimentação;
  // lead em andamento permanece onde está e mantém o responsável. O responsável vem do modal
  // (options.sdrId) ou, sem escolha, do contexto de quem importa — nunca do CSV.
  // NÃO dispara webhook de MKT por linha (evita inundar o n8n numa carga grande).
  // Processa em série para não competir na dedup de linhas repetidas do arquivo.
  importLeads = async (
    rows: ImportLeadRow[],
    ctx: LeadCreateContext,
    onProgress?: (done: number, total: number) => void,
    options: ImportLeadsOptions = {},
  ): Promise<ImportLeadsResult> => {
    if (!ctx?.workspaceSlug) throw new Error('Workspace sem slug.');
    const myEmail = ctx.userEmail || null;
    const autoSdrEmail    = (ctx.enterprise || ctx.teamRole === 'sdr') && myEmail ? myEmail : null;
    const autoCloserEmail = ctx.teamRole === 'closer' && myEmail ? myEmail : null;

    const results: ImportLeadOutcome[] = [];
    let created = 0, updated = 0, failed = 0, kept = 0;

    // Leads que a RPC devolveu com o responsável travado (já estão sendo
    // trabalhados). O responsável escolhido no modal NÃO pode passar por cima
    // deles — é exatamente o que fazia o lead sumir do board de quem o tinha.
    const ownerLockedIds = new Set<string>();

    // Enterprise: vincula cada lead importado à entidade empresa (Modelo A).
    // Cache por nome normalizado evita recriar/rebuscar a mesma empresa quando o
    // arquivo tem vários contatos da mesma companhia.
    const linkEmpresas = !!(ctx.enterprise && ctx.workspaceId && this.deps.resolveEmpresaId);
    const empresaIdByName = new Map<string, string | null>();

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      try {
        const res = (await this.deps.leadRepo.createExternalLead({
          workspace_slug: ctx.workspaceSlug,
          ...row.fields,
          tags: row.tagNames.length ? row.tagNames : null,
          sdr_email: autoSdrEmail,
          closer_email: autoCloserEmail,
        })) as {
          mode?: string;
          warnings?: string[] | null;
          lead_id?: string;
          card_action?: ImportCardAction;
          owner_locked?: boolean;
        } | undefined;

        const mode = res?.mode === 'updated' ? 'updated' : 'created';
        if (mode === 'updated') updated++; else created++;

        const cardAction = res?.card_action ?? 'none';
        if (cardAction === 'kept') kept++;
        if (res?.owner_locked && res?.lead_id) ownerLockedIds.add(res.lead_id);

        // Vínculo da empresa (não-fatal: se falhar, o lead já foi criado —
        // registra um aviso na linha, mas não conta como falha).
        const warnings = res?.warnings?.length ? [...res.warnings] : [];
        if (cardAction === 'kept') {
          warnings.push('Lead em andamento — permaneceu onde estava.');
        } else if (cardAction === 'revived') {
          warnings.push('Lead estava perdido/inativo — devolvido ao funil.');
        }
        if (linkEmpresas && res?.lead_id) {
          try {
            const key = row.fields.nome.trim().toLowerCase();
            if (!empresaIdByName.has(key)) {
              empresaIdByName.set(key, await this.deps.resolveEmpresaId!(ctx.workspaceId, row.fields.nome));
            }
            const empresaId = empresaIdByName.get(key) ?? null;
            if (empresaId) await this.deps.leadRepo.patchLead(res.lead_id, { empresa_id: empresaId });
          } catch (empErr) {
            warnings.push(`empresa não vinculada: ${(empErr as Error)?.message || 'erro'}`);
          }
        }

        results.push({
          rowNumber: row.rowNumber,
          label: row.label,
          status: mode,
          message: warnings.length ? warnings.join(' · ') : undefined,
          leadId: res?.lead_id ?? null,
          cardAction,
        });
      } catch (err) {
        failed++;
        results.push({
          rowNumber: row.rowNumber,
          label: row.label,
          status: 'error',
          message: (err as Error)?.message || 'Erro ao importar a linha.',
        });
      }
      onProgress?.(i + 1, rows.length);
    }

    // Responsável escolhido no modal: aplicado DEPOIS, de uma vez. A RPC só
    // aceita e-mail (que o front não tem) e a trigger de criação carimba quem
    // está importando — sobrescrever no fim é o que faz a escolha valer,
    // inclusive a de deixar sem ninguém. Não-fatal: os leads já entraram.
    // Os leads travados ficam de fora: quem já está com o lead na mão não perde
    // o card para o responsável da lista.
    const warnings: string[] = [];
    if (options.sdrId !== undefined) {
      const leadIds = results
        .map(item => item.leadId)
        .filter((id): id is string => !!id && !ownerLockedIds.has(id));
      try {
        if (leadIds.length) await this.deps.leadRepo.assignLeadsOwner(leadIds, options.sdrId);
      } catch (err) {
        warnings.push(`Leads importados, mas o responsável não foi aplicado: ${(err as Error)?.message || 'erro'}`);
      }
    }

    return { total: rows.length, created, updated, failed, kept, results, warning: warnings[0] };
  };

  // Atualiza o lead, notifica qualificação e propaga responsável aos cards ativos.
  updateLead = async (leadId: string, updates: LeadUpdateInput, prevStatus: string | null): Promise<void> => {
    await this.deps.leadRepo.patchLead(leadId, updates);
    if (updates.status === 'qualified' && prevStatus !== 'qualified') {
      this.deps.notifyMktConversion('qualificado', { leadId });
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'sdr_id')) {
      await this.deps.assignActiveCardsOwner(leadId, 'sdr_id', (updates.sdr_id as string | null) ?? null);
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'closer_id')) {
      await this.deps.assignActiveCardsOwner(leadId, 'closer_id', (updates.closer_id as string | null) ?? null);
    }
  };

  /**
   * Marca vários leads como perdidos usando a RPC transacional de cada card.
   * Leads são independentes: uma falha de permissão não desfaz os demais.
   * A concorrência limitada evita rajadas de centenas de requests ao Supabase.
   */
  markLeadsLost = async (
    input: BulkLossLeadInput[],
    rawReason: string,
    onProgress?: (progress: BulkLossProgress) => void,
    concurrency = 4,
  ): Promise<BulkLossResult> => {
    const reason = String(rawReason || '').trim();
    if (!reason) throw new Error('Informe o motivo da perda.');

    const leads = [...new Map(
      (input || []).filter(lead => !!lead?.id).map(lead => [lead.id, lead]),
    ).values()];
    const outcomes: Array<
      | { status: 'success'; leadId: string }
      | { status: 'failed'; failure: BulkLossFailure }
      | { status: 'skipped'; skipped: BulkLossSkipped }
    > = new Array(leads.length);
    let cursor = 0;
    let completed = 0;

    const processLead = async (lead: BulkLossLeadInput, index: number) => {
      const label = String(lead.label || lead.id);
      const cards = (lead.cards || []).filter(card =>
        !!card?.id
        && card.ativo !== false
        && card.status !== 'lost'
        && card.status !== 'won'
        && !card.lost_at
      );

      if (cards.length === 0) {
        outcomes[index] = {
          status: 'skipped',
          skipped: {
            leadId: lead.id,
            label,
            message: 'O lead não possui cards ativos elegíveis.',
          },
        };
        return;
      }

      try {
        const visited = new Set<string>();
        for (const card of cards) {
          if (visited.has(card.id)) continue;
          visited.add(card.id);
          if (card.linked_card_id) visited.add(card.linked_card_id);

          const result = await this.deps.markCardLost(card.id, reason);
          if (result?.success === false) {
            throw new Error(result.error || 'O banco recusou a perda do card.');
          }
          if (result?.linked_card_id) visited.add(result.linked_card_id);
        }

        await this.deps.leadRepo.patchLead(lead.id, { status: 'lost' });

        try {
          await this.deps.notifyMktConversion('perda', {
            leadId: lead.id,
            loss: {
              motivo: reason,
              origem: 'acao_em_massa',
              cards: cards.map(card => ({ card_id: card.id })),
            },
          });
        } catch {
          // O webhook é non-fatal: a perda persistida não deve ser revertida.
        }

        outcomes[index] = { status: 'success', leadId: lead.id };
      } catch (err) {
        outcomes[index] = {
          status: 'failed',
          failure: {
            leadId: lead.id,
            label,
            message: (err as Error)?.message || 'Erro ao marcar o lead como perdido.',
          },
        };
      }
    };

    const worker = async () => {
      while (cursor < leads.length) {
        const index = cursor;
        cursor += 1;
        const lead = leads[index];
        await processLead(lead, index);
        completed += 1;
        onProgress?.({ completed, total: leads.length, leadId: lead.id });
      }
    };

    const workerCount = Math.min(
      leads.length,
      Math.max(1, Math.min(6, Math.floor(concurrency) || 1)),
    );
    await Promise.all(Array.from({ length: workerCount }, () => worker()));

    const successfulLeadIds = outcomes
      .filter((outcome): outcome is { status: 'success'; leadId: string } => outcome?.status === 'success')
      .map(outcome => outcome.leadId);
    const failures = outcomes
      .filter((outcome): outcome is { status: 'failed'; failure: BulkLossFailure } => outcome?.status === 'failed')
      .map(outcome => outcome.failure);
    const skippedLeads = outcomes
      .filter((outcome): outcome is { status: 'skipped'; skipped: BulkLossSkipped } => outcome?.status === 'skipped')
      .map(outcome => outcome.skipped);

    return {
      total: leads.length,
      succeeded: successfulLeadIds.length,
      failed: failures.length,
      skipped: skippedLeads.length,
      successfulLeadIds,
      failures,
      skippedLeads,
    };
  };
  // Exclusão lógica: desativa o lead e todos os seus cards.
  deleteLead = async (leadId: string): Promise<void> => {
    await this.deps.leadRepo.deactivateLead(leadId);
    await this.deps.deactivateCardsByLead(leadId);
  };

  // Move o card de etapa e dispara os efeitos: webhook de mudança + auto-cancel
  // das atividades pendentes do card. Lança se o move em si falhar (a UI faz
  // rollback do otimista); webhook/cancel são non-fatal.
  moveCardStage = async ({ cardId, newStageId, oldStageId, leadId, mover }: MoveCardStageParams): Promise<void> => {
    let oldPendingIds: string[] = [];
    try {
      oldPendingIds = await this.deps.listPendingActivityIdsForCard(cardId);
    } catch (err) {
      console.warn('[LeadFlow] falha ao listar atividades pendentes do card:', err);
    }

    await this.deps.moveCardToStage(cardId, newStageId);

    try {
      if (leadId && oldStageId !== newStageId) {
        const ids = [oldStageId, newStageId].filter((id): id is string => !!id);
        const stageNames = ids.length ? await this.deps.getStageNames(ids) : {};
        this.deps.notifyMktConversion('mudanca-etapa', {
          leadId,
          stageChange: {
            card_id: cardId,
            etapa_anterior: oldStageId ? { id: oldStageId, nome: stageNames[oldStageId] || null } : null,
            etapa_nova: { id: newStageId, nome: stageNames[newStageId] || null },
            movido_por: mover,
          },
        });
      }
    } catch (whErr) {
      console.warn('[LeadFlow] webhook mudanca-etapa falhou:', (whErr as Error)?.message || whErr);
    }

    if (oldPendingIds.length > 0) {
      try {
        await this.deps.cancelActivitiesByIds(oldPendingIds);
      } catch (cancelErr) {
        console.warn('[LeadFlow] auto-cancel de atividades falhou:', cancelErr);
      }
    }
  };

  // Cria card no pipeline Closer, marca o lead qualified e notifica (se mudou).
  passToCloser = async ({ workspaceId, leadId, sdrId, prevStatus }: PassToCloserParams): Promise<void> => {
    await this.deps.leadRepo.passLeadToCloser(workspaceId, leadId, sdrId);
    if (prevStatus !== 'qualified') {
      this.deps.notifyMktConversion('qualificado', { leadId });
    }
  };
}
